// src/data/secureFetch.js
import { getAuth } from "firebase/auth";

const HTML_TAG_REGEX = /<[^>]+>/g;
const HTML_BREAK_REGEX = /<br\s*\/?>/gi;

const decodeBasicHtmlEntities = (value = "") =>
  value
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'");

const collapseWhitespace = (value = "") => value.replace(/\s+/g, " ").trim();

const normalizeErrorMessage = (rawMessage, status) => {
  const fallback = status
    ? `Request failed (${status}).`
    : "Request failed. Please try again.";
  if (typeof rawMessage !== "string" || !rawMessage.trim()) {
    return fallback;
  }

  const normalizedRaw = rawMessage.trim();
  if (/request entity too large|payloadtoolargeerror/i.test(normalizedRaw)) {
    return "Selected image is too large to upload. Please choose a smaller image.";
  }

  if (!/<[a-z][\s\S]*>/i.test(normalizedRaw)) {
    return collapseWhitespace(normalizedRaw);
  }

  const htmlText = collapseWhitespace(
    decodeBasicHtmlEntities(
      normalizedRaw
        .replace(HTML_BREAK_REGEX, " ")
        .replace(HTML_TAG_REGEX, " "),
    ),
  );

  if (/request entity too large|payloadtoolargeerror/i.test(htmlText)) {
    return "Selected image is too large to upload. Please choose a smaller image.";
  }

  if (!htmlText) {
    return fallback;
  }

  return htmlText.length > 240 ? `${htmlText.slice(0, 237)}...` : htmlText;
};

/**
 * Secure fetch wrapper that automatically includes Firebase ID token
 * in Authorization header for authenticated requests
 */
export async function secureFetch(
  path,
  { method = "GET", data, headers = {}, includeFirebaseAuth = true, ...opts } = {}
) {
  // Get Firebase auth instance
  const auth = getAuth();
  const user = auth.currentUser;

  // Get Firebase ID token if user is authenticated
  let authHeaders = { ...headers };
  const hasAuthorizationHeader = Object.keys(authHeaders).some(
    (headerName) => headerName.toLowerCase() === "authorization",
  );

  if (includeFirebaseAuth && user && !hasAuthorizationHeader) {
    try {
      const idToken = await user.getIdToken();
      authHeaders["Authorization"] = `Bearer ${idToken}`;
    } catch (error) {
      console.error("Error getting Firebase ID token:", error);
      // Continue without token - backend will handle authentication errors
    }
  }

  const envelope = { path, method, body: data, headers: authHeaders };
  const resp = await fetch("/.netlify/functions/proxy", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(envelope),
    ...opts,
  });

  const responseText = await resp.text();

  let respJSON = {};
  if (responseText) {
    try {
      respJSON = JSON.parse(responseText);
    } catch {
      // Fallback to plain text when upstream returns non-JSON
      respJSON = { message: responseText, error: responseText };
    }
  }

  if (!resp.ok) {
    const message =
      respJSON.message || respJSON.error || responseText || undefined;
    respJSON.message = normalizeErrorMessage(message, resp.status);
    respJSON.error = respJSON.message;
  }

  // Optionally attach HTTP status to the response
  respJSON.status = resp.status;
  respJSON.ok = resp.ok;

  return respJSON;
}
